import { brandApi } from '@/api/account-management/brandApi';
import { locationApi } from '@/api/account-management/locationApi';
import { organizationApi } from '@/api/account-management/organizationApi';
import { providerConfigurationApi } from '@/api/configuration/providerConfigurationApi';
import { type Brand } from '@/models/account-management/Brand';
import { type Location } from '@/models/account-management/Location';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useAppContext } from '../../../AppContext';
import { useLocalizedStrings } from '../../../localization/LocalizedStringsProvider';
import { useAlert } from '../../common/alert/AlertProvider';
import { useToast } from '../../common/toast/ToastProvider';
import { type CopiedConfig } from '../ProviderConfig';

interface Props {
    open: boolean;
    brandId?: string;
    currentLocationId?: string;
    locationType: number;
    onClose: () => void;
    copiedConfigRef?: React.MutableRefObject<Record<string, CopiedConfig>>;
    onCopied?: () => void;
}

const CopyConfigurationDialog = ({ open, brandId, currentLocationId, locationType, onClose, copiedConfigRef, onCopied }: Props) => {
    const strings = useLocalizedStrings();
    const toast = useToast();
    const alert = useAlert();
    const app = useAppContext();
    const { setValue } = useFormContext<Location>();

    // Super-admins may pull configs from a location in ANY brand of the viewed org; everyone else is pinned to
    // the current brand. The source picker below is only rendered for super-admins.
    const superAdmin = app.isSuperAdmin();

    const [sourceBrandId, setSourceBrandId] = useState(brandId ?? '');
    const [sourceId, setSourceId] = useState('');
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [copying, setCopying] = useState(false);

    useEffect(() => {
        if (!open) return;
        setSourceBrandId(brandId ?? '');
        setSourceId('');
        setSelected(new Set());
    }, [open, brandId]);

    // Brands of the viewed org — only super-admins get to switch source brand.
    const { data: brands } = useQuery<Brand[]>({
        queryKey: ['brands', app.organizationId],
        enabled: open && superAdmin && !!app.organizationId,
        queryFn: () => organizationApi.listBrands(app.organizationId!),
        staleTime: Infinity,
    });

    const { data: locations } = useQuery<Location[]>({
        queryKey: ['locations', sourceBrandId],
        enabled: open && !!sourceBrandId,
        queryFn: () => brandApi.listLocations(sourceBrandId),
        staleTime: Infinity,
    });

    const sources = (locations ?? []).filter((l) => l.id !== currentLocationId);

    // The type tab's label follows the CURRENT location's type.
    const typeLabel = locationType === 1 ? strings.retail : locationType === 2 ? strings.fieldService : strings.restaurant;

    const sections: { key: string; label: string }[] = [
        { key: 'apps', label: strings.apps },
        { key: 'type', label: typeLabel },
        { key: 'auth', label: strings.auth },
        { key: 'pos', label: strings.pos },
        { key: 'payment', label: strings.payment },
        { key: 'loyalty', label: strings.loyalty },
        { key: 'feedback', label: strings.feedback },
    ];

    const toggle = (key: string) => setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });

    const canCopy = !!sourceId && selected.size > 0 && !copying;

    // Provider configs are fetched per-kind; re-stamp the copied config onto the current location.
    const fetchCfg = async (kind: number) => {
        const c = await providerConfigurationApi.get(sourceId, kind);
        return c ? { ...c, locationId: currentLocationId ?? '' } : null;
    };

    // Copy a provider section (POS/Payment/Loyalty/Feedback) onto the current location. Records the copied
    // vendor/version/configurationJson so ProviderConfig's vendor-change effects don't overwrite the copied
    // JSON with a blank template when they see the new vendor/version land.
    const copySection = async (kind: number, field: 'posConfiguration' | 'paymentConfiguration' | 'loyaltyConfiguration' | 'feedbackConfiguration') => {
        const cfg = await fetchCfg(kind);
        if (cfg && copiedConfigRef) {
            const c = cfg as { vendor?: string; version?: string; configurationJson?: string; fieldNameMaps?: unknown[] };
            copiedConfigRef.current[field] = { vendor: c.vendor ?? '', version: c.version ?? '', configurationJson: c.configurationJson ?? '{}', fieldNameMaps: c.fieldNameMaps };
        }
        setValue(field, cfg as never, { shouldDirty: true });
    };

    // Payment is copied from the SOURCE LOCATION's full config (not the single-integration provider GET), so the
    // per-integration configs (integrationConfigurations — where integration-aware vendors like FreedomPay keep
    // their real config) come along. Records them on copiedConfigRef so ProviderConfig's reconcile keeps them.
    const copyPayment = (srcPayment: Location['paymentConfiguration']) => {
        if (!srcPayment) {
            setValue('paymentConfiguration', null as never, { shouldDirty: true });
            return;
        }
        if (copiedConfigRef) {
            copiedConfigRef.current['paymentConfiguration'] = {
                vendor: srcPayment.vendor ?? '',
                version: srcPayment.version ?? '',
                configurationJson: srcPayment.configurationJson ?? '{}',
                integrationConfigurations: srcPayment.integrationConfigurations,
            };
        }
        setValue('paymentConfiguration', { ...srcPayment, locationId: currentLocationId ?? '' } as never, { shouldDirty: true });
    };

    const handleCopy = async () => {
        const agree = await alert({
            title: strings.copyConfigurations,
            message: strings.copyConfigWarning,
            destructive: true,
            agreeButtonTitle: strings.proceed,
            closeButtonTitle: strings.cancel,
        });
        if (!agree) return;

        setCopying(true);
        try {
            // Apps / type / auth / payment all come from the source location record — fetch it once if needed.
            // (Payment needs the full location config so the per-integration configurations come along.)
            const needsLocation = selected.has('apps') || selected.has('type') || selected.has('auth') || selected.has('payment');
            const src = needsLocation ? await locationApi.get(sourceId) : null;

            if (selected.has('apps')) setValue('licensedApps', src?.licensedApps ?? [], { shouldDirty: true });
            if (selected.has('type')) {
                setValue('restaurant', src?.restaurant, { shouldDirty: true });
                setValue('retail', src?.retail, { shouldDirty: true });
            }
            if (selected.has('auth')) setValue('authenticationConfiguration', src?.authenticationConfiguration, { shouldDirty: true });

            if (selected.has('pos')) await copySection(0, 'posConfiguration');
            if (selected.has('payment')) copyPayment(src?.paymentConfiguration);
            if (selected.has('loyalty')) await copySection(3, 'loyaltyConfiguration');
            if (selected.has('feedback')) await copySection(4, 'feedbackConfiguration');

            // Signal the provider tabs to authoritatively re-apply the copied config (field maps + JSON).
            onCopied?.();

            toast({ severity: 'success', message: strings.copyConfigSuccess });
            onClose();
        } catch {
            toast({ severity: 'error', message: strings.copyConfigError });
        } finally {
            setCopying(false);
        }
    };

    return (
        <Dialog open={open} onClose={copying ? undefined : onClose} fullWidth maxWidth="sm">
            <DialogTitle>{strings.copyConfigurations}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    {superAdmin && (
                        <FormControl fullWidth>
                            <InputLabel id="copy-source-brand-label">{strings.copyConfigSourceBrand}</InputLabel>
                            <Select
                                labelId="copy-source-brand-label"
                                label={strings.copyConfigSourceBrand}
                                value={sourceBrandId}
                                onChange={(e) => { setSourceBrandId(e.target.value); setSourceId(''); }}
                            >
                                {(brands ?? []).map((b) => (
                                    <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                    <FormControl fullWidth>
                        <InputLabel id="copy-source-label">{strings.copyConfigSource}</InputLabel>
                        <Select labelId="copy-source-label" label={strings.copyConfigSource} value={sourceId} onChange={(e) => setSourceId(e.target.value)}>
                            {sources.map((l) => (
                                <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {sources.length === 0 && (
                        <Typography variant="body2" color="text.secondary">{strings.copyConfigNoLocations}</Typography>
                    )}

                    <Typography variant="subtitle2">{strings.copyConfigSections}</Typography>
                    <FormGroup>
                        {sections.map((s) => (
                            <FormControlLabel
                                key={s.key}
                                control={<Checkbox checked={selected.has(s.key)} onChange={() => toggle(s.key)} />}
                                label={s.label}
                            />
                        ))}
                    </FormGroup>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" onClick={onClose} disabled={copying}>{strings.cancel}</Button>
                <Button
                    variant="contained"
                    disabled={!canCopy}
                    onClick={handleCopy}
                    startIcon={copying ? <CircularProgress size={16} color="inherit" /> : undefined}
                >
                    {copying ? strings.copyConfigCopying : strings.copy}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CopyConfigurationDialog;
